import React from 'react';
import { Link } from 'react-router-dom';
import Button from '@components/common/Button';
import { FiHome, FiSearch, FiArrowLeft } from 'react-icons/fi';

/**
 * NotFound Component
 * 404 Error page for non-existent routes
 */
const NotFound = () => {
  return (
    <div className="min-h-screen bg-bukawa-light flex items-center justify-center px-4">
      <div className="max-w-2xl w-full text-center">
        {/* 404 Illustration */}
        <div className="mb-8">
          <div className="relative inline-block">
            {/* Large 404 */}
            <h1 className="text-9xl md:text-[200px] font-bold text-bukawa-primary-100 select-none">
              404
            </h1>
            
            {/* Floating elements */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="w-32 h-32 bg-bukawa-secondary rounded-full opacity-20 blur-3xl animate-pulse" />
            </div>
          </div>
        </div>

        {/* Message */}
        <div className="mb-8">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-bukawa-gray-900 mb-4">
            Page Not Found
          </h2>
          <p className="text-lg text-bukawa-gray-600 mb-2">
            Sorry, we couldn't find the page you're looking for.
          </p>
          <p className="text-bukawa-gray-600">
            The page might have been moved, deleted, or never existed.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <Button
            to="/"
            variant="primary"
            size="lg"
            icon={<FiHome />}
          >
            Go to Homepage
          </Button>
          <Button
            onClick={() => window.history.back()}
            variant="outline"
            size="lg"
            icon={<FiArrowLeft />}
          >
            Go Back
          </Button>
        </div>

        {/* Helpful Links */}
        <div className="bg-white rounded-2xl p-8 shadow-soft border border-bukawa-gray-200">
          <div className="flex items-center justify-center gap-2 mb-6">
            <FiSearch className="h-5 w-5 text-bukawa-primary" />
            <h3 className="text-xl font-semibold text-bukawa-gray-900">
              Looking for something?
            </h3>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Link
              to="/about"
              className="p-4 border border-bukawa-gray-200 rounded-lg hover:bg-bukawa-light hover:border-bukawa-primary-200 transition-all text-left group"
            >
              <h4 className="font-semibold text-bukawa-gray-900 mb-1 group-hover:text-bukawa-primary">
                About Us
              </h4>
              <p className="text-sm text-bukawa-gray-600">
                Learn about our cooperative
              </p>
            </Link>

            <Link
              to="/products"
              className="p-4 border border-bukawa-gray-200 rounded-lg hover:bg-bukawa-light hover:border-bukawa-primary-200 transition-all text-left group"
            >
              <h4 className="font-semibold text-bukawa-gray-900 mb-1 group-hover:text-bukawa-primary">
                Products
              </h4>
              <p className="text-sm text-bukawa-gray-600">
                View our maize flour products
              </p>
            </Link>

            <Link
              to="/services"
              className="p-4 border border-bukawa-gray-200 rounded-lg hover:bg-bukawa-light hover:border-bukawa-primary-200 transition-all text-left group"
            >
              <h4 className="font-semibold text-bukawa-gray-900 mb-1 group-hover:text-bukawa-primary">
                Services
              </h4>
              <p className="text-sm text-bukawa-gray-600">
                Explore our farmer services
              </p>
            </Link>

            <Link
              to="/contact"
              className="p-4 border border-bukawa-gray-200 rounded-lg hover:bg-bukawa-light hover:border-bukawa-primary-200 transition-all text-left group"
            >
              <h4 className="font-semibold text-bukawa-gray-900 mb-1 group-hover:text-bukawa-primary">
                Contact Us
              </h4>
              <p className="text-sm text-bukawa-gray-600">
                Get in touch with our team
              </p>
            </Link>
          </div>
        </div>

        {/* Support Text */}
        <p className="text-sm text-bukawa-gray-500 mt-8">
          If you believe this is an error, please{' '}
          <Link to="/contact" className="text-bukawa-primary hover:underline">
            contact us
          </Link>
          .
        </p>
      </div>
    </div>
  );
};

export default NotFound;